import { useState, useEffect } from 'react';

import { Button } from '@shadcn/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shadcn/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@shadcn/components/ui/card';
import { Label } from '@shadcn/components/ui/label';
import { Switch } from '@shadcn/components/ui/switch';
import { PlusIcon, EditIcon } from 'lucide-react';
import { TooltipProvider } from '@shadcn/components/ui/tooltip';
import { AddAmazonConfigDialog } from './components/AddAmazonConfigDialog.js';
import { MarketplaceDisplayNameDialog } from './components/MarketplaceDisplayNameDialog.js';
import { CopyableTruncatedCell } from './components/CopyableTruncatedCell.js';
import { fetchAmazonConfigs, deleteAmazonConfig } from './AmazonSellerConfig.service';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shadcn/components/ui/popover';


function AmazonSellerConfigsPage() {
  const [amazonConfigs, setAmazonConfigs] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDisplayNameDialogOpen, setIsDisplayNameDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletePopoverOpen, setDeletePopoverOpen] = useState(null);
  const [showDecrypted, setShowDecrypted] = useState(false);

  
  const [user, setUser] = useState(
    JSON.parse(localStorage.getItem('authUser')) || {}
  );
  
  const canManageAmazonConfigs =
    Array.isArray(user.permissions) && user.permissions.includes('ManageAmazonSellerConfigs');

  useEffect(() => {
    loadAmazonConfigs();
  }, [showDecrypted]);

  const loadAmazonConfigs = async () => {
    setLoading(true);
    try {
      const data = await fetchAmazonConfigs(!showDecrypted);
      setAmazonConfigs(data);
      setSelectedConfig((prev) => {
        if (!prev) return null;
        return data.find((config) => config._id === prev._id) || null;
      });
    } catch (error) {
      console.error('Failed to load amazon configs:', error);
      toast.error('Failed to load Amazon Seller Configs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfig = async (storeKey) => {
    if (!canManageAmazonConfigs) {
      toast.error('You do not have permission to delete Amazon Seller Configs');
      return;
    }

    try {
      await deleteAmazonConfig(storeKey);
      toast.success('Amazon Seller Config deleted successfully');
      loadAmazonConfigs();
    } catch (error) {
      console.error('Failed to delete amazon config:', error);
      toast.error(error.response?.body?.message || 'Failed to delete Amazon Seller Config');
    }
  };

  const handleAddSuccess = () => {
    setIsAddDialogOpen(false);
    loadAmazonConfigs();
  };

  const handleEditDisplayNames = (config) => {
    setSelectedConfig(config);
    setIsDisplayNameDialogOpen(true);
  };

  const handleDisplayNameSuccess = () => {
    setIsDisplayNameDialogOpen(false);
    setSelectedConfig(null);
    loadAmazonConfigs();
  };

  const getRegionName = (region) => {
    switch (region) {
      case 'na': return 'North America';
      case 'eu': return 'Europe';
      case 'fe': return 'Far East';
      default: return region;
    }
  };

  const secretColumnHeadClass = showDecrypted
    ? 'w-[300px] max-w-[300px]'
    : 'min-w-0 max-w-[240px]';
  const secretColumnCellClass = showDecrypted
    ? 'align-middle min-w-0 max-w-[300px] px-4 py-2'
    : 'align-middle min-w-0 max-w-[240px] overflow-hidden px-4 py-2';
  const marketplacesColumnHeadClass = showDecrypted
    ? 'w-[300px] max-w-[300px]'
    : 'min-w-0 max-w-[240px]';
  const marketplacesColumnCellClass = showDecrypted
    ? 'align-middle min-w-0 max-w-[300px] px-4 py-2'
    : 'align-middle min-w-0 max-w-[240px] px-4 py-2';
  const tableEmptyColSpan =
    6 +
    (canManageAmazonConfigs && showDecrypted ? 2 : 0) +
    (canManageAmazonConfigs ? 1 : 0);

  return (
    <div className="page-content">
      <div className="w-full px-3 mx-auto xl:max-w-[85%] mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-2xl font-bold flex items-center gap-2">
              <i className="bx bxl-amazon text-orange-500 text-2xl"></i>
              Amazon Seller Configs
            </CardTitle>
            <div className="flex flex-row flex-wrap items-center justify-end gap-3">
              {canManageAmazonConfigs && (
                <div className="flex items-center gap-2">
                  <Switch
                    id="amazon-show-decrypted"
                    checked={showDecrypted}
                    onCheckedChange={setShowDecrypted}
                  />
                  <Label htmlFor="amazon-show-decrypted" className="text-sm font-normal cursor-pointer m-0">
                    Show decrypted values
                  </Label>
                </div>
              )}
              {canManageAmazonConfigs && (
                <Button onClick={() => setIsAddDialogOpen(true)}>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Config
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="spinner-border text-primary m-1" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : (
              <TooltipProvider delayDuration={250}>
                <div className={showDecrypted ? 'min-w-0 overflow-x-auto' : 'min-w-0 overflow-x-hidden'}>
                  <Table className={showDecrypted ? 'w-max min-w-full' : undefined}>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[120px]">Store Key</TableHead>
                        <TableHead className="min-w-[100px]">Region</TableHead>
                        <TableHead className="min-w-[100px]">Seller ID</TableHead>
                        <TableHead className="min-w-[200px]">Client ID</TableHead>
                        {canManageAmazonConfigs && showDecrypted && (
                          <>
                            <TableHead className={secretColumnHeadClass}>Refresh token</TableHead>
                            <TableHead className={secretColumnHeadClass}>Client secret</TableHead>
                          </>
                        )}
                        <TableHead className={marketplacesColumnHeadClass}>Marketplaces</TableHead>
                        <TableHead className="min-w-[80px]">Status</TableHead>
                        {canManageAmazonConfigs && <TableHead className="min-w-[88px]">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {amazonConfigs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={tableEmptyColSpan} className="text-center py-8">
                            No Amazon Seller Configs found. {canManageAmazonConfigs && 'Click "Add Config" to create one.'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        amazonConfigs.map((config) => (
                          <TableRow key={config._id}>
                            <TableCell className="align-middle min-w-0 max-w-[200px] overflow-hidden px-4 py-2 font-medium">
                              {config.storeKey}
                            </TableCell>
                            <TableCell className="align-middle whitespace-nowrap px-4 py-2">
                              {getRegionName(config.region)}
                            </TableCell>
                            <TableCell className="align-middle min-w-0 max-w-[140px] overflow-hidden px-4 py-2">
                              {config.sellerId || 'N/A'}
                            </TableCell>
                            <TableCell className="align-middle min-w-0 max-w-[220px] overflow-hidden px-4 py-2">
                              <CopyableTruncatedCell value={config.clientId} />
                            </TableCell>
                            {canManageAmazonConfigs && showDecrypted && (
                              <>
                                <TableCell className={secretColumnCellClass}>
                                  <CopyableTruncatedCell value={config.refreshToken || '—'} />
                                </TableCell>
                                <TableCell className={secretColumnCellClass}>
                                  <CopyableTruncatedCell value={config.clientSecret || '—'} />
                                </TableCell>
                              </>
                            )}
                            <TableCell className={marketplacesColumnCellClass}>
                              <CopyableTruncatedCell
                                multiline
                                value={
                                  config.marketplaces?.length > 0
                                    ? config.marketplaces.map(m => `${m.displayName || m.name} (${m.countryCode})`).join(', ')
                                    : ''
                                }
                                empty="N/A"
                                mono={false}
                              />
                            </TableCell>
                            <TableCell className="align-middle whitespace-nowrap px-4 py-2">
                              <div className="flex min-h-9 w-full items-center justify-center">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  config.isActive
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {config.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </TableCell>
                            {canManageAmazonConfigs && (
                              <TableCell className="align-middle px-4 py-2">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEditDisplayNames(config)}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200 hover:border-blue-300 transition-colors"
                                    title="Edit Display Names"
                                  >
                                    <EditIcon className="h-4 w-4" />
                                  </Button>
                                  <Popover open={deletePopoverOpen === config._id} onOpenChange={(open) => setDeletePopoverOpen(open ? config._id : null)}>
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300 transition-colors"
                                      >
                                        <i className="bx bx-trash text-lg"></i>
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-48 p-3" align="end" sideOffset={5}>
                                      <div className="space-y-3">
                                        <div className="text-center">
                                          <i className="bx bx-error-circle text-red-500 text-2xl mb-2"></i>
                                          <p className="text-sm font-medium text-gray-800">Delete "{config.storeKey}"?</p>
                                        </div>
                                        <div className="flex gap-2 justify-center">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setDeletePopoverOpen(null)}
                                            className="px-3 py-1 h-8"
                                          >
                                            <i className="bx bx-x"></i>
                                          </Button>
                                          <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => {
                                              handleDeleteConfig(config.storeKey);
                                              setDeletePopoverOpen(null);
                                            }}
                                            className="px-3 py-1 h-8"
                                          >
                                            <i className="bx bx-check"></i>
                                          </Button>
                                        </div>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TooltipProvider>
            )}
          </CardContent>
        </Card>

        <AddAmazonConfigDialog
          open={isAddDialogOpen}
          onOpenChange={setIsAddDialogOpen}
          onSuccess={handleAddSuccess}
        />
        
        <MarketplaceDisplayNameDialog
          open={isDisplayNameDialogOpen}
          onOpenChange={setIsDisplayNameDialogOpen}
          config={selectedConfig}
          onSuccess={handleDisplayNameSuccess}
        />
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default AmazonSellerConfigsPage;
